import { usePopoverPosition } from "@/shared/hooks/use-popover-position";
import { useConfirmPopoverStore } from "@/shared/stores";
import { Alert } from "@spz/ui/components/alert";
import { Button } from "@spz/ui/components/button";
import { Popover, PopoverContent } from "@spz/ui/components/popover";
import { Typography } from "@spz/ui/components/typography";
import { determinePopoverSide } from "@spz/ui/lib/popover";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export const ConfirmPopover = () => {
  const { t } = useTranslation();
  const { isOpen, description, side, anchorRect, onConfirm, closePopover } =
    useConfirmPopoverStore();
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    const handlePosition = () => {
      usePopoverPosition({
        isOpen,
        contentRef,
        anchorRect,
        side,
        mounted,
        determinePopoverSide,
      });
    };

    handlePosition();
    requestAnimationFrame(handlePosition);

    window.addEventListener("resize", handlePosition);
    window.addEventListener("scroll", handlePosition, true);

    return () => {
      window.removeEventListener("resize", handlePosition);
      window.removeEventListener("scroll", handlePosition, true);
    };
  }, [isOpen, anchorRect, side, mounted]);

  if (!mounted) return null;

  return (
    <Popover open={isOpen} onOpenChange={closePopover}>
      <PopoverContent
        ref={contentRef}
        side={side}
        forceMount
        className="gap-3 flex flex-col w-[298px]"
        style={{ transform: "none" }}
      >
        <div className="flex gap-3 items-center">
          <Alert variant="critical" withIcon />
          <Typography variant="caption" className="text-red-900">
            {description}
          </Typography>
        </div>
        <div className="flex gap-2 flex-row w-full">
          <Button variant="secondary" className="w-full" onClick={closePopover}>
            {t("no")}
          </Button>
          <Button
            className="w-full"
            onClick={() => {
              onConfirm();
              closePopover();
            }}
          >
            {t("yes")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
